/**
 * CopilotTurnList — the scrolling transcript: user bubbles + Merlin replies
 * (CopilotTurnView), with the smart scroll both Quality surfaces share.
 *
 * Smart scroll: when a NEW turn starts, bring the user's question to the TOP
 * so the reply reads from its beginning (not pinned to the end of a long
 * answer); while the SAME turn streams, only stick to the bottom if the user
 * is already near it, so scrolling up to read isn't yanked back down.
 */
import { Fragment, useEffect, useRef, type ReactNode } from 'react';

import { MessageTimestamp } from '../agentConsole/MessageTimestamp';
import type { CopilotTurn } from '../../pages/qualityCopilotTurns';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { CopilotTurnView } from './CopilotTurnView';
import { AskToolChips } from './AskToolChips';
import { askToolLines } from './askChipView';
import styles from './CopilotTurns.module.css';

export interface CopilotTurnListProps {
  turns: CopilotTurn[];
  /**
   * Hide user turns the pipeline typed (the investigator's seeded kickoff):
   * on a thread's workspace Merlin's filing already opens the detail pane,
   * so the seed is noise there (Scott, review bs63ro).
   */
  hideSystemSeeded?: boolean;
  loading?: boolean;
  /** Rendered when there are no turns and nothing is loading. */
  emptyState?: ReactNode;
  onReplay: (turnId: string, spoken: string) => void;
  onStop: () => void;
  onReview?: (orderId: string) => void;
  speakingId: string | null;
  isSpeaking: boolean;
  isSynthesizing: boolean;
  showUsage: boolean;
  conversationId: string | null;
  strongestModelId: string | null;
  strongestModelLabel: string | null;
  /** 'page' centres a reading column; 'pane' fills its host edge to edge. */
  variant?: 'page' | 'pane';
  /** Show each reply's spoken rundown as a small Listen button. */
  compactVoice?: boolean;
  /** The item's asks: when given, each "Drafted a question to X" Merlin
   *  made gets a live sent / answered chip (proposal 2). */
  asks?: DoorDataRequestRead[];
  /** The item or case the chat sits on, recorded with a draft chip's click. */
  askTrackContext?: Record<string, unknown>;
}

export function CopilotTurnList({
  turns,
  hideSystemSeeded = false,
  loading = false,
  emptyState,
  onReplay,
  onStop,
  onReview,
  speakingId,
  isSpeaking,
  isSynthesizing,
  showUsage,
  conversationId,
  strongestModelId,
  strongestModelLabel,
  variant = 'page',
  compactVoice = false,
  asks,
  askTrackContext,
}: CopilotTurnListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevTurnCountRef = useRef(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const grew = turns.length > prevTurnCountRef.current;
    // A conversation loading in (from nothing) jumps; a new turn glides. A
    // smooth scroll on load left the pane on an old message when the reader
    // looked (UX loop turn 3).
    const firstFill = prevTurnCountRef.current === 0;
    prevTurnCountRef.current = turns.length;
    if (grew) {
      const userEls = el.querySelectorAll<HTMLElement>('[data-qcp-user]');
      const lastUser = userEls[userEls.length - 1];
      if (lastUser && typeof lastUser.scrollIntoView === 'function') {
        lastUser.scrollIntoView({ block: 'start', behavior: firstFill ? 'auto' : 'smooth' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    } else if (el.scrollHeight - el.scrollTop - el.clientHeight < 140) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns]);

  const visible = hideSystemSeeded
    ? turns.filter((t) => !(t.role === 'user' && t.systemSeeded))
    : turns;

  return (
    <div
      className={`${styles.convo} ${variant === 'pane' ? styles.convoPane : ''}`}
      ref={scrollRef}
    >
      {loading && (
        <div className={styles.status}>
          <span className={styles.spinner} />
          <span>Loading conversation…</span>
        </div>
      )}
      {!loading && visible.length === 0 && emptyState}
      {visible.map((t) =>
        t.role === 'user' ? (
          <div key={t.id} className={styles.turn} data-qcp-user="">
            <div className={styles.userRow}>
              <div className={styles.userBubble}>{t.text}</div>
            </div>
            <div className={styles.userCap}>
              {t.viaVoice ? '🎙️ transcribed from voice' : '⌨️ typed'}
              {t.attachments && t.attachments.length > 0
                ? ` · 📎 ${t.attachments.length} file${
                    t.attachments.length > 1 ? 's' : ''
                  }`
                : ''}
              {t.createdAt ? <> · <MessageTimestamp at={t.createdAt} /></> : null}
            </div>
          </div>
        ) : (
          <Fragment key={t.id}>
            <CopilotTurnView
              turn={t}
              onReview={onReview}
              onReplay={onReplay}
              onStop={onStop}
              speaking={isSpeaking && speakingId === t.id}
              synthesizing={isSynthesizing && speakingId === t.id}
              showUsage={showUsage}
              conversationId={conversationId}
              strongestModelId={strongestModelId}
              strongestModelLabel={strongestModelLabel}
              compactVoice={compactVoice}
            />
            {asks && !t.streaming && <AskToolChips lines={askToolLines(t.blocks, asks)} trackContext={askTrackContext} />}
          </Fragment>
        ),
      )}
    </div>
  );
}

export default CopilotTurnList;
